import { useEffect, ReactNode } from 'react';

import classNames from 'classnames';
import MuiSnackbar from '@mui/material/Snackbar';

import { Portal } from '../Portal';

import { useStyles } from './Snackbar.styles';

/**
 * Свойства компонента {@link Snackbar}
 */
export interface SnackbarProps {
  /**
   * Указывает, открыт ли Snackbar.
   */
  open: boolean;

  /**
   * Сообщение для отображения в Snackbar.
   */
  message?: ReactNode | ReactNode[];

  /**
   * Действие для отображения в Snackbar (например, кнопка).
   */
  action: ReactNode | ReactNode[];

  /**
   * Вариант Snackbar, влияющий на его стиль отображения.
   *
   * @defaultValue `'static'`
   */
  variant?: 'static' | 'sticky';

  /**
   * Дополнительное имя класса CSS для применения к Snackbar.
   */
  className?: string;

  /**
   * Вертикальное положение Snackbar в вьюпорте.
   *
   * @defaultValue `'top'`
   */
  zPosition?: 'top' | 'bottom';

  /**
   * Флаг, скрывающий сообщение.
   */
  hideMessage?: boolean;
}

/**
 * Компонент Snackbar для отображения сообщений и действий.
 * Может быть позиционирован статически или с прилипанием и размещён в верхней или нижней части вьюпорта.
 *
 * @public
 * @param open - Указывает, открыт ли Snackbar.
 * @param message - Сообщение для отображения в Snackbar.
 * @param action - Действие для отображения в Snackbar (например, кнопка).
 * @param variant - Вариант Snackbar, влияющий на его стиль отображения.
 * @param className - Дополнительное имя класса CSS для применения к Snackbar.
 * @param zPosition - Вертикальное положение Snackbar в вьюпорте.
 * @param hideMessage - Флаг, скрывающий сообщение.
 * @returns Компонент Snackbar.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SnackbarProps}
 */
export const Snackbar = ({
  open,
  message,
  action,
  className,
  variant = 'static',
  zPosition = 'top',
  hideMessage,
}: SnackbarProps) => {
  const { classes } = useStyles();

  useEffect(() => {
    const webimButtonCorner = Array.from(
      document.getElementsByClassName('webim-button-corner'),
    )[0];

    if (webimButtonCorner) {
      if (open) {
        webimButtonCorner.classList.add('webim-button-corner-button');
      } else {
        webimButtonCorner.classList.remove('webim-button-corner-button');
      }
    }

    const webimChat = document.getElementById('webim_chat');
    if (webimChat) {
      if (open) {
        webimChat.classList.add('webim-chat-button');
      } else {
        webimChat.classList.remove('webim-chat-button');
      }
    }

    return () => {
      if (webimButtonCorner) {
        webimButtonCorner.classList.remove('webim-button-corner-button');
      }

      if (webimChat) {
        webimChat.classList.remove('webim-chat-button');
      }
    };
  }, [open]);

  let contentClassName = classes.snackbarContent;

  if (variant === 'sticky') {
    contentClassName = classes.snackbarContentSticky;
  }

  if (zPosition === 'top') {
    contentClassName = classes.snackbarContentPositionTop;
  }

  return (
    <Portal>
      <MuiSnackbar
        anchorOrigin={{ vertical: zPosition, horizontal: 'center' }}
        className={classNames(classes.snackbar, 'aqa_snackbar', className, {
          [classes.snackbarHideMessage]: !message || hideMessage,
        })}
        open={open}
        message={message}
        action={action}
        ContentProps={{
          className: contentClassName,
        }}
      />
    </Portal>
  );
};
